import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import i18next from "i18next";
import { toast } from "react-toastify";
import type { EventDetails, EventSeries } from "src/types";

import { getState } from "@/features/helpers";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";

import { getIsFirstLoading } from "../requests/requests-reducer";

const get = endpoints();

export const fetchSeries = createAsyncThunk<EventSeries[], string>(
  "relatedEvents/fetchSeries",
  async (eventExternalId: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch.fetchJSON<EventSeries[]>(
        get("EVENT_SERIES")(eventExternalId)
      );

      if (isError(response)) {
        toast.error(response.description, { toastId: response.description });
        return rejectWithValue(response);
      }

      return response;
    } catch (error) {
      toast.error(error?.message || i18next.t("common.error-response.general"));
      return rejectWithValue(null);
    }
  }
);

export const fetchSeriesEvents = createAsyncThunk<EventDetails[], string>(
  "relatedEvents/fetchSeriesEvents",
  async (seriesExternalId: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch.fetchJSON<EventDetails[]>(
        get("EVENT_SERIES_EVENTS")(seriesExternalId)
      );

      if (isError(response)) {
        toast.error(response.description, { toastId: response.description });
        return rejectWithValue(response);
      }

      return response;
    } catch (error) {
      toast.error(error?.message || i18next.t("common.error-response.general"));
      return rejectWithValue(null);
    }
  }
);

const relatedEventsSlice = createSlice({
  name: "relatedEvents",
  initialState: {
    eventSeries: [],
    eventSeriesLoadingStatus: "",
    eventSeriesEvents: {},
    eventSeriesEventsLoadingStatus: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchSeries.pending, (state) => {
      state.eventSeriesLoadingStatus = "pending";
    });
    builder.addCase(fetchSeries.fulfilled, (state, action) => {
      state.eventSeries = [
        ...(Array.isArray(action.payload) ? action.payload : []).map(
          (series) => ({
            externalId: series.external_id,
            name: series.name,
          })
        ),
      ];
      state.eventSeriesLoadingStatus = "loaded";
    });
    builder.addCase(fetchSeries.rejected, (state) => {
      state.eventSeriesLoadingStatus = "error";
    });
    builder.addCase(fetchSeriesEvents.pending, (state, action) => {
      state.eventSeriesEventsLoadingStatus = {
        ...state.eventSeriesEventsLoadingStatus,
        [action.meta.arg]: "loading",
      };
    });
    builder.addCase(fetchSeriesEvents.fulfilled, (state, action) => {
      state.eventSeriesEvents = {
        ...state.eventSeriesEvents,
        [action.meta.arg]: action.payload,
      };
      state.eventSeriesEventsLoadingStatus = {
        ...state.eventSeriesEventsLoadingStatus,
        [action.meta.arg]: "loaded",
      };
    });
    builder.addCase(fetchSeriesEvents.rejected, (state, action) => {
      state.eventSeriesEventsLoadingStatus = {
        ...state.eventSeriesEventsLoadingStatus,
        [action.meta.arg]: "error",
      };
    });
  },
});

export const getAllSeries = createSelector(
  getState,
  (state) => state.relatedEvents.eventSeries
);

export const getSeriesEvents = createSelector(
  getState,
  (state) => state.relatedEvents.eventSeriesEvents
);

export const getIsSeriesFirstLoading = createSelector(getState, (state) => {
  return getIsFirstLoading(state, fetchSeries);
});

export const getIsSeriesEventsInStore = createSelector(
  [getState, (_state, seriesExternalId: string) => seriesExternalId],
  (state, seriesExternalId) => {
    return seriesExternalId in state.relatedEvents.eventSeriesEvents;
  }
);

export const getIsGivenSeriesEventsLoadingStatus = createSelector(
  [getState, (_state, seriesExternalId: string) => seriesExternalId],
  (state, seriesExternalId): string => {
    return state.relatedEvents.eventSeriesEventsLoadingStatus[seriesExternalId];
  }
);

export const getEventSeriesLoadingStatus = createSelector(getState, (state) => {
  return state.relatedEvents.eventSeriesLoadingStatus;
});

export default relatedEventsSlice.reducer;
